import React, { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import {
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip
} from "@mui/material";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import CloseIcon from "@mui/icons-material/Close";
import * as NGL from "ngl";

const MemoForceGraph2D = React.memo(ForceGraph2D);

// react-force-graph-2d needs an explicit pixel width/height (it doesn't fill
// its container automatically) -- these canvases were all hardcoded to
// 580x450, which overflows its box the moment that box is narrower than
// 580px (any tablet/mobile viewport, and even some desktop windows once two
// sit side by side in a 50/50 split). Measuring each canvas's own container
// keeps it exactly matched to whatever width that box actually has at any
// breakpoint, mirroring the same pattern GraphExplorer.js uses for its 2D graph.
function useContainerWidth(defaultWidth) {
  const ref = useRef(null);
  const [width, setWidth] = useState(defaultWidth);

  useEffect(() => {
    if (!ref.current) return undefined;

    const updateWidth = () => {
      if (!ref.current) return;
      const { width: measured } = ref.current.getBoundingClientRect();
      setWidth(Math.max(240, Math.floor(measured) - 16));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export default function GraphView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [queryTopRef, queryTopWidth] = useContainerWidth(560);
  const [patternTopRef, patternTopWidth] = useContainerWidth(560);
  const [queryMapRef, queryMapWidth] = useContainerWidth(560);
  const [patternMapRef, patternMapWidth] = useContainerWidth(560);
  const graphCanvasHeight = 450;
  const rowData = location.state?.rowData || null;
  const returnTo = location.state?.returnTo || -1;
  const returnState = location.state?.returnState;
  const hoveredLinkRef = useRef(null);
  const [ligandInfo, setLigandInfo] = useState(null);
  const modalViewerRef = useRef(null);
  const modalStageRef = useRef(null);
  const [structureViewer, setStructureViewer] = useState({
    open: false,
    pdbId: "",
    title: "",
    highlightChains: []
  });
  const [viewerStatus, setViewerStatus] = useState({
    loading: false,
    error: ""
  });
  const [modalViewerReady, setModalViewerReady] = useState(false);

  const hoveredTargetTopRef = useRef(null);
  const hoveredLigandTopRef = useRef(null);
  const hoveredTargetMapRef = useRef(null);
  const hoveredLigandMapRef = useRef(null);

  const handleBack = () => {
    if (typeof returnTo === "string") {
      navigate(returnTo, { state: returnState });
      return;
    }

    navigate(returnTo);
  };

  const resetModalStage = () => {
    if (modalStageRef.current) {
      try {
        modalStageRef.current.dispose();
      } catch (error) {
        console.warn("NGL modal stage cleanup warning:", error);
      }
      modalStageRef.current = null;
    }

    if (modalViewerRef.current) {
      modalViewerRef.current.innerHTML = "";
    }
  };

  const openStructureViewer = (pdbId, title, highlightChains = []) => {
    resetModalStage();
    setModalViewerReady(false);
    setViewerStatus({
      loading: true,
      error: ""
    });
    setStructureViewer({
      open: true,
      pdbId,
      title,
      highlightChains
    });
  };

  const closeStructureViewer = () => {
    resetModalStage();
    setModalViewerReady(false);
    setViewerStatus({
      loading: false,
      error: ""
    });
    setStructureViewer(prev => ({
      ...prev,
      open: false
    }));
  };

  

  const preparedTargetGraph = useMemo(() => {
    if (!rowData?.target_graph) return null;

    return {
      nodes: rowData.target_graph.nodes.map(n => ({
        ...n,
        label: `
          <div style="background-color:#1E293B;padding:6px;border-radius:6px;color:#E5E7EB;font-size:12px">
            <b>ResName:</b> ${n.residue_name}<br/>
            <b>ResNum:</b> ${n.residue_number}<br/>
            <b>AtomName:</b> ${n.atom_name}<br/>
            <b>Chain:</b> ${n.chain}<br/>
            <b>AtomType:</b> ${n.atom_type}
          </div>
        `
      })),
      links: rowData.target_graph.links.map((l, i) => ({
        ...l,
        label: l.interaction_type?.join(" | "),
        interactionTypes: l.interaction_type || [],
        _labelOffset: (i % 3 - 1) * 6
      }))
    };
  }, [rowData]);


  const preparedLigandGraph = useMemo(() => {
    if (!rowData?.ligand_graph) return null;

    return {
      nodes: rowData.ligand_graph.nodes.map(n => ({
        ...n,
        label: `
          <div style="background-color:#1E293B;padding:6px;border-radius:6px;color:#E5E7EB;font-size:12px">
            <b>ResName:</b> ${n.residue_name}<br/>
            <b>ResNum:</b> ${n.residue_number}<br/>
            <b>AtomName:</b> ${n.atom_name}<br/>
            <b>Chain:</b> ${n.chain}<br/>
            <b>AtomType:</b> ${n.atom_type}
          </div>
        `
      })),
      links: rowData.ligand_graph.links.map((l, i) => ({
        ...l,
        label: l.interaction_type?.join(" | "),
        interactionTypes: l.interaction_type || [],
        _labelOffset: (i % 3 - 1) * 6
      }))
    };
  }, [rowData]);


  const buildGraphData = (rowData) => {
    const nodes = new Map();
    const links = [];

    const targetEdges = rowData.highlight.target_edges;
    const ligandEdges = rowData.highlight.ligand_edges;

    targetEdges.forEach(e => {
        nodes.set(e.source, { id: e.source });
        nodes.set(e.target, { id: e.target });

        links.push({
        source: e.source,
        target: e.target,
        group: "target"
        });
    });

    ligandEdges.forEach(e => {
        nodes.set(e.source, { id: e.source });
        nodes.set(e.target, { id: e.target });

        links.push({
            source: e.source,
            target: e.target,
            group: "ligand"
        });
    });

    return {
        nodes: Array.from(nodes.values()),
        links
    };
  };

  const graphData = useMemo(() => {
    if (!rowData) return { nodes: [], links: [] };
    return buildGraphData(rowData);
  }, [rowData]);

  /*const drawLink = (link, ctx) => {
    if (!link.label) return;

    const sx = link.source.x;
    const sy = link.source.y;
    const tx = link.target.x;
    const ty = link.target.y;

    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;

    let angle = Math.atan2(ty - sy, tx - sx);
    if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI;

    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const ox = (-dy / len) * 1;
    const oy = (dx / len) * 1;

    const fontSize = 1;
    ctx.font = `${fontSize}px Arial`;
    ctx.fillStyle = "#000";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.strokeStyle = "#0c0404";
    ctx.lineWidth = 0.05;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    ctx.save();
    ctx.translate(mx + ox, my + oy);
    ctx.rotate(angle);
    ctx.fillText(link.label, 0, 0);
    ctx.restore();
  };*/

  /*const makeDrawLink2 = (hoverRef) => (link, ctx) => {
    const { x: sx, y: sy } = link.source;
    const { x: tx, y: ty } = link.target;

    // draw line
    ctx.strokeStyle = "#aaa";
    ctx.lineWidth = 0.3;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    // hover isolation
    if (link !== hoverRef.current) return;

    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;

    let angle = Math.atan2(ty - sy, tx - sx);
    if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI;

    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const offset = 4;
    const ox = (-dy / len) * offset;
    const oy = ( dx / len) * offset;

    ctx.save();
    ctx.translate(mx + ox, my + oy);
    ctx.rotate(angle);

    ctx.font = "2px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const text = link.label ;
    const w = ctx.measureText(text).width / 3;
    const h = 3;
    const pad = 1.5;

    ctx.fillStyle = "rgb(33,44,69)";
    ctx.fillRect(-w/2-pad, -h/2-pad, w+pad*2, h+pad*2);

    ctx.fillStyle = "#fff";
    ctx.fillText(text, 0, 0);

    ctx.restore();
  };*/


  const makeDrawLink  = (hoverRef) => (link, ctx) => {
    // draw line always
    const sx = link.source.x;
    const sy = link.source.y;
    const tx = link.target.x;
    const ty = link.target.y;

    // always draw the line
    ctx.strokeStyle = "#aaa";
    ctx.lineWidth = 0.3;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    // only draw label if hovered
    if (link !== hoverRef.current) return;

    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;

    let angle = Math.atan2(ty - sy, tx - sx);
    if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI;

    
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const offset = 4; // distance from edge
    const ox = (-dy / len) * offset;
    const oy = ( dx / len) * offset;

    const text = link.label;
    

    //const ox = (-dy / len) * 1;
    //const oy = (dx / len) * 1;

    ctx.save();
    ctx.translate(mx + ox, my + oy);
    ctx.rotate(angle);

    // measure text
    const textWidth = ctx.measureText(text).width/3;
    const padding = 1.5;
    const textHeight = 3;

    // 🔹 background rectangle
    ctx.fillStyle = "rgb(33, 44, 69)"; // hover background color
    ctx.fillRect(
        -textWidth / 2 - padding,
        -textHeight / 2 - padding,
        textWidth + padding * 2,
        textHeight + padding * 2
    );


    // 🔹 text
    ctx.font = "2px Arial";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(link.label, 0, 0);

    ctx.restore();
    };


    

     
  useEffect(() => {
    if (!rowData?.PDBID_Lig) return;

    const pdbId = rowData.PDBID_Lig.toUpperCase();

    fetch(`https://data.rcsb.org/rest/v1/core/entry/${pdbId}`)
      .then(res => res.json())
      .then(data => {
        console.log("Ligand info:", data);
        setLigandInfo(data);
      })
      .catch(err => console.error("Info fetch error:", err));
  }, [rowData?.PDBID_Lig]);

  useEffect(() => {
    if (!structureViewer.open || !modalViewerReady || !modalViewerRef.current || !structureViewer.pdbId) {
      return undefined;
    }

    let isMounted = true;
    let animationFrameId;
    let resizeTimeoutId;
    let stage;

    setViewerStatus({
      loading: true,
      error: ""
    });

    animationFrameId = window.requestAnimationFrame(() => {
      if (!isMounted || !modalViewerRef.current) {
        return;
      }

      stage = new NGL.Stage(modalViewerRef.current, {
        backgroundColor: "white"
      });
      modalStageRef.current = stage;
      stage.setParameters({
        clipNear: 0,
        clipFar: 100,
        cameraType: "perspective",
        quality: "medium",
        sampleLevel: 0,
        impostor: true
      });

      const selectedChains = (structureViewer.highlightChains || []).filter(Boolean);
      const highlightedSele = selectedChains.length > 0
        ? selectedChains.map(chain => `:${chain}`).join(" or ")
        : "";

      stage.loadFile(`https://files.rcsb.org/download/${structureViewer.pdbId.toUpperCase()}.pdb`, {
        defaultRepresentation: false,
        firstModelOnly: true
      }).then(comp => {
        if (!isMounted) {
          return;
        }

        comp.addRepresentation("cartoon", {
          sele: "protein",
          color: "lightgrey"
        });

        if (highlightedSele) {
          comp.addRepresentation("cartoon", {
            sele: highlightedSele,
            color: "chainid"
          });
          comp.addRepresentation("ball+stick", {
            sele: highlightedSele,
            color: "chainid",
            scale: 1.2
          });
        }

        comp.autoView();
        stage.viewerControls.zoom(-0.15);
        stage.handleResize();
        resizeTimeoutId = window.setTimeout(() => stage.handleResize(), 180);
        setViewerStatus({
          loading: false,
          error: ""
        });
      }).catch(() => {
        if (!isMounted) {
          return;
        }

        setViewerStatus({
          loading: false,
          error: `Unable to load structure ${structureViewer.pdbId}.`
        });
      });
    });

    const handleResize = () => {
      if (modalStageRef.current) {
        modalStageRef.current.handleResize();
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      isMounted = false;
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(resizeTimeoutId);
      window.removeEventListener("resize", handleResize);
      if (modalStageRef.current === stage) {
        modalStageRef.current = null;
      }

      if (stage) {
        try {
          stage.dispose();
        } catch (error) {
          console.warn("NGL modal dispose warning:", error);
        }
      }

      if (modalViewerRef.current) {
        modalViewerRef.current.innerHTML = "";
      }
    };
  }, [modalViewerReady, structureViewer]);

  

  

 

    const {
      highlightLigandNodes,
      nonMappingLigandChains,
      validLigandChains
    } = useMemo(() => {
      if (
        !rowData?.highlight?.node_mapping ||
        !rowData?.ligand_graph?.nodes
      ) {
        return {
          highlightLigandNodes: new Set(),
          nonMappingLigandChains: [],
          validLigandChains: []
        };
      }

      const selectedTargetChain = rowData.Target_Chain;
      const nodeMappings = rowData.highlight.node_mapping;

      // 1️⃣ ligand chains actually used for this target chain
      const validLigandChains = [
        ...new Set(
          nodeMappings
            .filter(m => m.targ_chain === selectedTargetChain)
            .map(m => m.lig_chain)
        )
      ];

      console.log("valid ligand chains:", validLigandChains);

      // 2️⃣ all ligand chains present in the ligand graph
      const allLigandChains = new Set(
        rowData.ligand_graph.nodes.map(n => n.chain)
      );

      console.log("all ligand chains:", [...allLigandChains]);

      // 3️⃣ ligand chains NOT involved in mapping
      const nonMappingLigandChains = [...allLigandChains].filter(
        chain => !validLigandChains.includes(chain)
      );

      // 4️⃣ highlight ligand nodes from valid ligand chains
      const highlightLigandNodes = new Set(
        nodeMappings
          .filter(
            m =>
              m.targ_chain === selectedTargetChain &&
              validLigandChains.includes(m.lig_chain)
          )
          .map(m => String(m.lig_node))
      );

      return {
        highlightLigandNodes,
        nonMappingLigandChains,
        validLigandChains
      };
    }, [rowData]);


    
  
     const percent = Math.round(rowData.pick_score * 100);

    

    useEffect(() => {
    if (!preparedLigandGraph || !highlightLigandNodes) return;

        console.log("Highlight ligand nodes:", [...highlightLigandNodes]);
        console.log("Graph node ids:", preparedLigandGraph.nodes.map(n => n.id));
        console.log("Full highlight object:", rowData.highlight);
    }, [preparedLigandGraph, highlightLigandNodes]);


  if (!rowData || !preparedTargetGraph) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>No graph data available</Typography>
        <Button onClick={handleBack}>Back</Button>
      </Box>
    );
  }
 
 

  return (
    <Box sx={{ padding: { xs: "1.5rem 4%", md: "8rem 10%" }, width: "100%" }}>
      <Typography variant="h4" gutterBottom>
        Graph Visualization
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
          width: "100%"
        }}
      >
        {/* LEFT COLUMN */}
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2
            }}
          >
            {/* Query Graph */}
            <Box
              ref={queryTopRef}
              sx={{
                width: { xs: "100%", md: "50%" },
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 1,
                height: "500px",
                overflow: "hidden",
                backgroundColor: "#fafafa"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
                  Graph Query: {rowData.PDBID_Targ}, Target Chain: {rowData.Target_Chain}
                </Typography>
                <Tooltip title="Open Query structure in NGL Viewer">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openStructureViewer(
                        rowData.PDBID_Targ,
                        `Graph Query: ${rowData.PDBID_Targ}`,
                        [rowData.Target_Chain]
                      )
                    }
                  >
                    <ViewInArIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <MemoForceGraph2D
                graphData={preparedTargetGraph}
                nodeLabel={n => n.label}
                nodeColor={n => (n.is_ligand ? "#8BC34A" : "#4C9F9E")}
                linkCanvasObjectMode={() => "replace"}
                //linkCanvasObject={drawLink}
                //onLinkHover={link => {hoveredLinkRef.current = link; }}
                linkCanvasObject={makeDrawLink(hoveredTargetTopRef)}
                onLinkHover={l => (hoveredTargetTopRef.current = l)}


                width={queryTopWidth}
                height={graphCanvasHeight}
              />
            </Box>

            {/* Graph Pattern Match */}
            <Box
              ref={patternTopRef}
              sx={{
                width: { xs: "100%", md: "50%" },
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 1,
                height: "500px",
                overflow: "hidden",
                backgroundColor: "#fafafa"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
                  Graph Pattern Match: {rowData.PDBID_Lig}
                </Typography>
                <Tooltip title="Open Pattern Match structure in NGL Viewer">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openStructureViewer(
                        rowData.PDBID_Lig,
                        `Graph Pattern Match: ${rowData.PDBID_Lig}`,
                        nonMappingLigandChains.length === 0
                          ? validLigandChains
                          : nonMappingLigandChains
                      )
                    }
                  >
                    <ViewInArIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <MemoForceGraph2D
                graphData={preparedLigandGraph}
                nodeLabel={n => n.label}
                nodeColor={n => (n.is_ligand ? "#8BC34A" : "#4C9F9E")}

                linkCanvasObjectMode={() => "replace"}
                linkCanvasObject={makeDrawLink(hoveredLigandTopRef)}
                onLinkHover={l => (hoveredLigandTopRef.current = l)}
                //linkCanvasObject={drawLink}
                //onLinkHover={link => {hoveredLinkRef.current = link; }}

                width={patternTopWidth}
                height={graphCanvasHeight}
              />
            </Box>
          </Box>

            {/* Mapping Section */}
            <Box
                sx={{
                mt: 2,
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 2,
                backgroundColor: "#fafafa"
                }}
            >
                <Typography variant="h5" sx={{backgroundColor:"#2c2a2a", color:"#fff", padding:"5px", marginBottom:"10px"}}>Graph Mapping</Typography>
                {/* Row 1: Graphs side by side */}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} ref={queryMapRef}>
                        <Typography variant="h6">Graph Query: {rowData.PDBID_Targ}, Target Chain: {rowData.Target_Chain}</Typography>
                        <MemoForceGraph2D
                            graphData={preparedTargetGraph}
                            nodeLabel={n => n.label}
                            //nodeColor={n => (n.is_ligand ? "#8BC34A" : "#4C9F9E")}

                            nodeCanvasObject={(node, ctx) => {
                                const isHighlighted = node.chain === rowData.Target_Chain;
                                const radius = isHighlighted ? 5 : 3;

                                const fillColor = node.is_ligand ? "#8BC34A" : "#4C9F9E";
                                
                                ctx.beginPath();
                                //ctx.arc(node.x, node.y, isHighlighted ? 5 : 3, 0, 2 * Math.PI);
                                ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
                                ctx.fillStyle = fillColor;
                                //ctx.fillStyle = isHighlighted ? "red" : "#4C9F9E";
                                ctx.fill();

                                // red border only if highlighted
                                if (isHighlighted) {
                                    ctx.lineWidth = 2;
                                    ctx.strokeStyle = "red";
                                    ctx.stroke();
                                }
                            }}


                            linkCanvasObjectMode={() => "replace"}
                            linkCanvasObject={makeDrawLink(hoveredTargetMapRef)}
                            onLinkHover={l => (hoveredTargetMapRef.current = l)}
                            //linkCanvasObject2={drawLink}
                            //onLinkHover={link => {hoveredLinkRef.current = link; }}

                            width={queryMapWidth}
                            height={graphCanvasHeight}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} ref={patternMapRef}>
                        <Typography variant="h6">Graph Pattern Match: {rowData.PDBID_Lig}</Typography>
                        <MemoForceGraph2D
                            graphData={preparedLigandGraph}
                            nodeLabel={n => n.label}
                            //nodeColor={n => (n.is_ligand ? "#8BC34A" : "#4C9F9E")}

                            nodeCanvasObject={(node, ctx) => {
                                const isHighlighted = highlightLigandNodes.has(node.id);
                                const radius = isHighlighted ? 5 : 3;

                                const fillColor = node.is_ligand ? "#8BC34A" : "#4C9F9E";

                                ctx.beginPath();
                                ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
                                //ctx.arc(node.x, node.y, isHighlighted ? 5 : 3, 0, 2 * Math.PI);
                                ctx.fillStyle = fillColor;
                                //ctx.fillStyle = isHighlighted ? "red" : "#4C9F9E";
                                ctx.fill();

                                // red border only if highlighted
                                if (isHighlighted) {
                                    ctx.lineWidth = 2;
                                    ctx.strokeStyle = "red";
                                    ctx.stroke();
                                }
                            }}

                            linkCanvasObjectMode={() => "replace"}
                            linkCanvasObject={makeDrawLink(hoveredLigandMapRef)}
                            onLinkHover={l => (hoveredLigandMapRef.current = l)}
                            //linkCanvasObject2={drawLink}
                            //onLinkHover={link => {hoveredLinkRef.current = link; }}

                            width={patternMapWidth}
                            height={graphCanvasHeight}
                        />
                    </Grid>
                </Grid>
                
                <Divider sx={{ mb: 2, mt: 2 }} />

                

                {/* Row 2: Chain mappings */}
                <Box sx={{ mt: 3}}>
                            <Box sx={{ mt: 2, p: 1, border: "1px solid #ccc", borderRadius: 1 }}>
                                <Typography variant="h6">Other Information</Typography>
                                <Grid container spacing={2} >
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2">
                                        <b>Target Size:</b> {rowData?.Targ_Size}
                                    </Typography>

                                    <Typography variant="subtitle2">
                                        <b>Ligand Size:</b> {rowData?.Lig_Size}
                                    </Typography>

                                    <Typography variant="subtitle2">
                                        <b>Ligand Chains:</b> {rowData?.Ligand_Chains.join(", ")}
                                    </Typography>

                                    <Typography variant="subtitle2">
                                        <b>Confidence Score: </b>{rowData?.pick_score.toFixed(3)}
                                    </Typography>

                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2">
                                        <b>Mapped ligand chains:</b> {validLigandChains.join(", ")}
                                    </Typography>

                                    <Typography variant="subtitle2">
                                        <b>Non-mapping ligand chains:</b> {nonMappingLigandChains.join(", ") || "None"}
                                    </Typography>


                                    <Typography sx={{ mt: 1, color: "#1523e6", fontWeight: "bold" }}>
                                        Potential Swap Ligand Chain(s):{" "}
                                        {(nonMappingLigandChains.length === 0
                                            ? validLigandChains
                                            : nonMappingLigandChains
                                        ).join(", ") || "None"}
                                    </Typography>

                                    <Typography sx={{ mt: 1, color: "green", fontWeight: "bold" }}>
                                        Target Chain: {rowData.PDBID_Targ}, {rowData.Target_Chain}
                                        <br />
                                        Predicted Chain(s): {rowData.PDBID_Lig}, {" "}
                                        {(nonMappingLigandChains.length === 0
                                            ? validLigandChains
                                            : nonMappingLigandChains
                                        ).join(", ") || "None"}
                                    </Typography>

                                    {nonMappingLigandChains.length === 0 && (
                                        <Typography sx={{ color: "blue" }}>
                                            ✔ Target chain fully mapped — all ligand chains are swap candidates
                                        </Typography>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                                        <CircularProgress
                                          variant="determinate"
                                          value={percent}
                                          size={200}
                                          thickness={7}
                                        />
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                          }}
                                        >
                                          <Typography fontWeight="bold" sx={{fontSize:"24px"}}>{percent}%</Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Grid>
                                </Grid>
                                
                            </Box>
                </Box>
            </Box>
        </Box>

        {/* RIGHT COLUMN */}
        <Box
          sx={{
            flex: 1,
            border: "1px solid #ddd",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#fdfdfd"
          }}
        >
            <Typography variant="h5" sx={{backgroundColor:"#2c2a2a", color:"#fff", padding:"5px"}}>Details Panel: Ligand PDB Details</Typography>
            {ligandInfo && (
            <Box sx={{ mt: 2 }}>
                <Typography><b>PDB ID:</b> {rowData.PDBID_Lig}</Typography>
                <Typography><b>Title:</b> {ligandInfo?.struct?.title || "N/A"}</Typography>
                <Typography><b>Method:</b> {ligandInfo?.exptl?.[0]?.method || "N/A"}</Typography>
                <Typography>
                <b>Resolution:</b>{" "}
                {ligandInfo?.rcsb_entry_info?.resolution_combined?.[0] || "N/A"}
                </Typography>
                <Typography>
                <b>Release Date:</b>{" "}
                {ligandInfo?.rcsb_accession_info?.initial_release_date || "N/A"}
                </Typography>
            </Box>
            )}

            <Divider sx={{ mb: 2, mt: 2 }} />

            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
              Interactive structure viewing is available from the small NGL icon on
              both the Graph Query and Graph Pattern Match cards.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", lineHeight: 1.7 }}>
              The embedded right-side viewers are disabled to keep the page responsive
              while you inspect structures in the modal.
            </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>

      <Dialog
        open={structureViewer.open}
        onClose={closeStructureViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "78vh"
          }
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        TransitionProps={{
          onEntered: () => setModalViewerReady(true),
          onExit: () => setModalViewerReady(false),
          onExited: () => {
            setModalViewerReady(false);
            resetModalStage();
          }
        }}
      >
        <DialogTitle sx={{ pr: 7 }}>
          {structureViewer.title || "Protein Structure Viewer"}
          <IconButton
            aria-label="Close structure viewer"
            onClick={closeStructureViewer}
            sx={{
              position: "absolute",
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Interactive NGL Viewer for {structureViewer.pdbId}
          </Typography>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "70vh",
              minHeight: "520px",
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid #ddd"
            }}
          >
            {viewerStatus.loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.85)",
                  zIndex: 1
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {viewerStatus.error && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 3,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  zIndex: 1
                }}
              >
                <Typography color="error">{viewerStatus.error}</Typography>
              </Box>
            )}
            <Box
              key={`${structureViewer.pdbId}-${structureViewer.title}`}
              ref={modalViewerRef}
              sx={{
                width: "100%",
                height: "100%"
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
